import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import { createStore } from 'zustand'

import { immer } from 'zustand/middleware/immer'

interface RoomsParticipantDistributionState {
  roomsParticipantDistribution: RoomsParticipantDistribution
}

interface RoomsParticipantDistributionActions {
  addRoom: () => void
  deleteRoom: (roomIndex: number) => void
  deleteRooms: (roomIndex: number[]) => void
  setAdults: (roomIndex: number, adults: number) => void
  addChild: (roomIndex: number, date: Date) => void
  setChild: (roomIndex: number, index: number, date: Date) => void
  removeChild: (roomIndex: number) => void
  addBaby: (roomIndex: number, date: Date) => void
  setBaby: (roomIndex: number, index: number, date: Date) => void
  removeBaby: (roomIndex: number) => void
  setRooms: (roomsDistribution: RoomsParticipantDistribution) => void
  removeBabyOnIndex: (roomIndex: number, babyIndex: number) => void
  removeChildOnIndex: (roomIndex: number, childIndex: number) => void
}

export type RoomsParticipantDistributionStore = RoomsParticipantDistributionState & RoomsParticipantDistributionActions

const initialState = {
  roomsParticipantDistribution: [],
}

export function createRoomsParticipantDistributionStore(initState: RoomsParticipantDistributionState) {
  return createStore<RoomsParticipantDistributionStore>()(
    immer(set => ({
      ...initState,
      addRoom: () => {
        set(({ roomsParticipantDistribution }) => {
          roomsParticipantDistribution.push({
            adults: 0,
            children: [],
            babies: [],
          })
        })
      },
      deleteRoom: (roomIndex) => {
        set(({ roomsParticipantDistribution }) => {
          roomsParticipantDistribution.splice(roomIndex, 1)
        })
      },
      deleteRooms: (roomIndices) => {
        set(({ roomsParticipantDistribution }) => {
          roomIndices
            .sort((a, b) => b - a)
            .forEach((index) => {
              if (roomsParticipantDistribution[index]) {
                roomsParticipantDistribution.splice(index, 1)
              }
            })
        })
      },
      setRooms: (roomsDistribution) => {
        set(() => ({
          roomsParticipantDistribution: roomsDistribution,
        }))
      },
      setAdults: (roomIndex, adults) => {
        set(({ roomsParticipantDistribution }) => {
          roomsParticipantDistribution[roomIndex].adults = adults
        })
      },
      addChild: (roomIndex, date) => {
        set(({ roomsParticipantDistribution }) => {
          roomsParticipantDistribution[roomIndex].children.push(date)
        })
      },
      setChild: (roomIndex, index, date) => {
        set(({ roomsParticipantDistribution }) => {
          roomsParticipantDistribution[roomIndex].children[index] = date
        })
      },
      removeChild: (roomIndex) => {
        set(({ roomsParticipantDistribution }) => {
          roomsParticipantDistribution[roomIndex].children.pop()
        })
      },
      addBaby: (roomIndex, date) => {
        set(({ roomsParticipantDistribution }) => {
          roomsParticipantDistribution[roomIndex].babies.push(date)
        })
      },
      setBaby: (roomIndex, index, date) => {
        set(({ roomsParticipantDistribution }) => {
          roomsParticipantDistribution[roomIndex].babies[index] = date
        })
      },
      removeBaby: (roomIndex) => {
        set(({ roomsParticipantDistribution }) => {
          roomsParticipantDistribution[roomIndex].babies.pop()
        })
      },
      removeBabyOnIndex: (roomIndex: number, babyIndex: number) => {
        set(({ roomsParticipantDistribution }) => {
          const babiesArrCopy = [...roomsParticipantDistribution[roomIndex].babies]
          const updatedBabiesArr = babiesArrCopy.filter((_, index) => index !== babyIndex)
          roomsParticipantDistribution[roomIndex].babies = updatedBabiesArr
        })
      },
      removeChildOnIndex: (roomIndex: number, childIndex: number) => {
        set(({ roomsParticipantDistribution }) => {
          const childrenArrCopy = [...roomsParticipantDistribution[roomIndex].children]
          const updatedChildrenArr = childrenArrCopy.filter((_, index) => index !== childIndex)
          roomsParticipantDistribution[roomIndex].children = updatedChildrenArr
        })
      },
    })),
  )
}

export function emptyRoomIndicesSelector(roomsParticipantDistribution: RoomsParticipantDistribution) {
  return roomsParticipantDistribution.reduce<number[]>((acc, room, index) => {
    if (room.adults === 0 && room.children.length === 0 && room.babies.length === 0) {
      acc.push(index)
    }
    return acc
  }, [])
}
export const roomsParticipantsDistributionStore = createRoomsParticipantDistributionStore(initialState)
